import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStudioList } from '@/api/base';
import { Button } from '@/components/kit/Button';
import { TextLink } from '@/components/kit/Controls';
import { FilterBar, MultiFilterChip, SearchInput } from '@/components/kit/Filters';
import { PageHeader, Segments } from '@/components/kit/Layout';
import { Who } from '@/components/kit/Person';
import { formatPhone } from '@/lib/phone';
import { ListPanel } from '../shared/ListPanel';
import { useDebouncedSearch } from '../shared/useDebouncedSearch';
import { joinList, listOf, useListParams } from '../shared/useListParams';
import { NewStudioModal } from './StudioDialogs';

/**
 * Список студий (БЛ 11.7, viewStudios в макете): Студия · Город ·
 * Основной контакт · Команд · Участников · Внимание.
 */
export function StudiosListPage() {
  const navigate = useNavigate();
  const { values, page, set } = useListParams(['state', 'cityId', 'q'] as const);
  const state = values.state === 'archive' ? 'archive' : 'active';
  const [text, setText] = useDebouncedSearch(values.q, (q) => set({ q }));
  const [creating, setCreating] = useState(false);
  const list = useStudioList({ ...values, state, page });
  const data = list.data;

  const head = (
    <tr>
      <th className="c-sname srt">
        Студия <span className="ar">▲</span>
      </th>
      <th className="c-scity">Город</th>
      <th className="c-scont">Основной контакт</th>
      <th className="r c-num">Команд</th>
      <th className="r c-num">Участников</th>
      <th className="c-att">Внимание</th>
    </tr>
  );

  return (
    <div className="main">
      <PageHeader title="Студии" count={data?.segments.active}>
        <Button tone="primary" onClick={() => setCreating(true)}>
          ＋ Добавить студию
        </Button>
      </PageHeader>

      <Segments
        value={state}
        onChange={(s) => set({ state: s === 'active' ? null : s, cityId: null })}
        items={[
          { value: 'active', label: 'Активные', count: data?.segments.active },
          { value: 'archive', label: 'Архив', count: data?.segments.archive },
        ]}
      />

      <FilterBar search={<SearchInput value={text} onChange={setText} placeholder="Название, контакт, телефон, почта" />}>
        <MultiFilterChip
          label="Город"
          values={listOf(values.cityId)}
          onChange={(v) => set({ cityId: joinList(v) })}
          options={(data?.facets.cities ?? []).map((f) => ({ value: f.value, label: f.label, side: f.count }))}
        />
      </FilterBar>

      <ListPanel
        head={head}
        cols={6}
        loading={list.isLoading}
        error={list.error}
        onRetry={() => void list.refetch()}
        errorTitle="Не удалось загрузить студии."
        empty={
          data && !data.items.length ? (
            values.q || values.cityId ? (
              <>
                По этим условиям студий не нашлось.
                <br />
                <TextLink onClick={() => set({ cityId: null, q: null })}>Сбросить фильтры</TextLink>
              </>
            ) : state === 'archive' ? (
              'В архиве студий нет.'
            ) : (
              <>
                Студий пока нет.
                <br />
                <TextLink onClick={() => setCreating(true)}>Добавить студию</TextLink>
              </>
            )
          ) : null
        }
        paging={
          data
            ? { page, pageSize: data.pageSize, total: data.total, shown: data.items.length, onPage: (p) => set({ page: String(p) }) }
            : undefined
        }
        rows={data?.items.map((s) => (
          <tr key={s.id} className="clk" onClick={() => navigate(`/studios/${s.id}`)}>
            <td data-col="primary">
              <Who initials={s.initials} name={s.name} />
            </td>
            <td data-col="hide">{s.city.name}</td>
            <td data-col="hide">
              {s.mainContact ? (
                <>
                  {s.mainContact.shortName}
                  <div className="sub num">{formatPhone(s.mainContact.phone)}</div>
                </>
              ) : (
                <span className="na">—</span>
              )}
            </td>
            <td className="r num" data-col="hide">
              {s.teamsCount}
            </td>
            <td className="r num" data-col="secondary">
              {s.participantsCount}
            </td>
            <td data-col="hide">{s.signal ? <span className="wtx">нет контактов</span> : null}</td>
          </tr>
        ))}
      />

      {creating ? <NewStudioModal onClose={() => setCreating(false)} onCreated={(id) => navigate(`/studios/${id}`)} /> : null}
    </div>
  );
}
